/**
 * Utility functions for Pinia stores
 * Provides reusable error handling and state management helpers
 */

/**
 * Check if error is an authentication error (401)
 */
export const isAuthError = (error: any): boolean => {
  return error.response?.status === 401
}

/**
 * Extract error message from API response with fallback
 */
export const extractErrorMessage = (error: any, fallbackMessage: string): string => {
  return error.response?.data?.detail || error.message || fallbackMessage
}

/**
 * Generic async action wrapper with automatic loading/error state management
 *
 * @example
 * await withLoadingState(
 *   () => this.loading = true,
 *   () => this.loading = false,
 *   async () => {
 *     const response = await api.getData()
 *     this.data = response.data
 *   },
 *   (error) => this.error = extractErrorMessage(error, 'Failed to fetch')
 * )
 */
export async function withLoadingState<T>(
  setLoading: (loading: boolean) => void,
  action: () => Promise<T>,
  onError?: (error: any) => void
): Promise<T | undefined> {
  setLoading(true)

  try {
    return await action()
  } catch (error) {
    if (onError) {
      onError(error)
    }
    throw error
  } finally {
    setLoading(false)
  }
}

/**
 * Handles authentication errors
 * Returns true if error is auth error, false otherwise
 */
export const handleAuthError = (error: any, setError?: (msg: string) => void): boolean => {
  if (isAuthError(error)) {
    if (setError) {
      setError('Current user is not authorized to access this resource')
    }
    return true
  }
  return false
}
